import type {PerkAccessADto} from '@cohort/admin-schemas/perkAccesses';
import type {PerkAnalyticsUsageADto} from '@cohort/admin-schemas/perkAnalytics';
import Button from '@cohort/merchants/components/buttons/Button';
import DigitalAssetReferenceComponent from '@cohort/merchants/components/references/DigitalAssetReferenceComponent';
import PerkReferenceComponent from '@cohort/merchants/components/references/PerkReferenceComponent';
import Table from '@cohort/merchants/components/Table';
import type {UserPerksFilters} from '@cohort/merchants/hooks/filters/userPerksFilters';
import PerkAccessStatusBadge from '@cohort/merchants/pages/users/user/perks/PerkAccessStatusBadge';
import PerkUsageDetailsModal from '@cohort/merchants/pages/users/user/perks/PerkUsageDetailsModal';
import {formatDate} from '@cohort/shared/utils/format';
import {ListMagnifyingGlass} from '@phosphor-icons/react';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

const UserPerksTable: React.FC<{
  filters: UserPerksFilters;
  isLoading: boolean;
  perkAccesses: PerkAccessADto[];
}> = ({filters, isLoading, perkAccesses}) => {
  const [perkAnalyticsUsagesToShow, setPerkAnalyticsUsagesToShow] =
    useState<Array<PerkAnalyticsUsageADto> | null>(null);
  const {t} = useTranslation('pages', {keyPrefix: 'users.user.perks.userPerksTable'});

  return (
    <div>
      <Table
        headers={[
          {label: t('tableHeaderPerk'), width: '30%'},
          {label: t('tableHeaderDigitalAsset'), width: '30%'},
          {label: t('tableHeaderAvailableSince'), sortingKey: 'availableSince'},
          {label: t('tableHeaderStatus')},
        ]}
        isLoading={isLoading}
        emptyState={{
          isEmpty: perkAccesses.length === 0,
          title: t('titleEmpty'),
          description: t('descriptionEmpty'),
        }}
        sortBy={filters.sort}
        onSort={filters.setSort as (sortingKey: string) => void}
      >
        {perkAccesses.map(perkAccess => (
          <tr key={perkAccess.id} className="last:pr-0! *:py-3">
            <td>
              <PerkReferenceComponent
                internalName={perkAccess.perk.internalName}
                perkId={perkAccess.perk.id}
                perkType={perkAccess.perk.type}
                perkIntegrationId={perkAccess.perk.perkIntegrationId}
              />
            </td>
            <td>
              <DigitalAssetReferenceComponent
                tokenId={perkAccess.digitalAssetCollection.tokenId}
                internalName={perkAccess.digitalAssetCollection.internalName}
              />
            </td>
            <td className="text-slate-500">{formatDate(perkAccess.createdAt)}</td>
            <td className="pr-0">
              <div className="flex flex-col items-start space-y-2">
                <PerkAccessStatusBadge status={perkAccess.status} />
              </div>
            </td>
            <td>
              {perkAccess.usages !== undefined && perkAccess.usages.length > 0 && (
                <Button
                  onClick={() => setPerkAnalyticsUsagesToShow(perkAccess.usages ?? null)}
                  variant="secondary"
                  className="ml-2 space-x-1"
                  size="xs"
                >
                  <ListMagnifyingGlass size={18} /> <div>{t('buttonShowDetails')}</div>
                </Button>
              )}
            </td>
          </tr>
        ))}
      </Table>
      {perkAnalyticsUsagesToShow !== null && (
        <PerkUsageDetailsModal
          onClose={() => setPerkAnalyticsUsagesToShow(null)}
          title={t('perkUsageDetailsModalTitle')}
          perkAnalyticsUsages={perkAnalyticsUsagesToShow}
        />
      )}
    </div>
  );
};
export default UserPerksTable;
